import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { makeStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '../../../../context/theme/theme';
import { Space } from '../../../common';
import Layout from '../Layout';
import configs from '../../../../configs/configuration';
import { useLanguage } from '../../../../context/language/language';
import { deliveryDateTextResolver, deliveryMethodResolver } from '../../../../utils/deliveryMethod';
import { useCarts } from '../../../common/hook/useCart';
import useDeliveryData from '../../../common/hook/useDeliveryData';
import {
  DELIVERY_ADDRESS,
  DELIVERY_SLOT,
  SELECT_STORE,
  SUMMARY_DELIVERY
} from '../../../../constant/deliveryFlow';
import useDeliveryControlFlow from '../../../common/hook/useDeliveryControlFlow';
import Display from '../../../common/Display';
import ClockIcon from '../../../../medias/icon/Component/ClockIcon';
import ChevronRightIcon from '../../../../medias/icon/Component/ChevronRightIcon';
import useResponsive from '../../../../utils/responsive';
import { useSite } from '../../../../context/siteContext';
import { defaultModalSize } from '../modalSize';
import { getIsPhoenix, getStoreOpenTime } from '../../../../utils/utils';
import StoreCloseWarningV2 from '../../../Order/OrderSummary/StoreCloseWarningV2';
import { trackPageLoad } from '../../../../utils/adobeAnalytics';
import useStoreLocation from '../../../common/hook/useStoreLocation';
import { determineStoreType } from '../../../../store/bootstrap/api';
import { selectCurrentWebSiteCode } from '../../../../store/stores/selector';
import { is1HrStore } from '../../../../utils/useSpecialStore';
import { deliveryUpdateForm } from '../../../../store/deliveryFlow/actions';
import { trackClickConfirmSelectDelivery } from '../../../../utils/eventTracking';

const isPhoenix = getIsPhoenix();

const Container = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const ItemContainer = styled.div(({ theme }) => ({
  padding: 16,
  border: `1px solid ${theme.colors.grey100}`,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
}));

const ItemHeader = styled.p(({ theme }) => ({
  ...theme.fonts.header6,
  color: theme.colors.grey700,
}));

const ItemDetail = styled.p(({ theme }) => ({
  ...theme.fonts.textS,
  color: theme.colors.grey550,
  width: theme.isMobile ? '100%' : '95%',
  ...theme.isMobile && {
    ...theme.fonts.textM
  }
}));

const ItemPermanantClosed = styled.div(({ theme }) => ({
  ...theme.fonts.textS,
  color: theme.colors.yellow600,
  width: theme.isMobile ? '100%' : '95%',
  display: 'flex',
  marginTop: '4px'
}));

const ItemChangeText = styled.p(({ theme }) => ({
  ...theme.fonts.textM,
  color: theme.colors.green600,
  fontWeight: 500,
  cursor: 'pointer',
}));

const ButtonContainer = styled.div(() => ({
  flex: 1,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
}));

const BtnCancel = styled(Button)(({ theme }) => ({
  ...theme.radius.m,
  ...theme.fonts.header6,
  marginRight: 8,
  backgroundColor: theme.colors.grey75,
  height: 44,
  minWidth: 159,
  textTransform: 'none',
  color: theme.colors.grey700,
  
  ...theme.isMobile && {
    ...theme.fonts.header4,
    width: 'calc(50% - 8px)'
  }
}));

const BtnSubmit = styled(Button)(({ theme, disabled }) => ({
  ...theme.radius.m,
  ...theme.fonts.header6,
  minWidth: 159,
  backgroundColor: theme.colors.green600,
  color: disabled ? theme.colors.grey500 : theme.colors.white,
  height: 44,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.colors.green400,
  },
  ...theme.isMobile && {
    ...theme.fonts.header4,
    width: '50%'
  }
}));

const useStyle = makeStyles((theme) => ({
  root: {
    flex: 1,
  },
  slotEmptyContainer: {
    marginTop: 8,
    borderRadius: 8,
    padding: 8,
    backgroundColor: theme.grey25,
    height: 'auto',
  },
  clock: {
    width: 20,
    height: 20,
    fill: '#F9BE00',
    marginRight: 8
  },
  changeText: {
    ...theme.fonts.textS,
    color: theme.gray700,
  },
  chevronRightIcon: {
    fill: theme.gray700,
    marginLeft: 8,
    fontSize: '1rem'
  }
}));

const DeliverSlotIsEmpty = ({
  changeHandler
}) => {
  const word = useLanguage();
  return (
    <ItemContainer>
      <div style={{ flex: 1 }}>
        <ItemHeader>
          {word('OrderSummary.Slot.Collect')}
        </ItemHeader>
      </div>
      <ItemChangeText onClick={changeHandler}>{word('DeliveryFlow.ConclusionPage.SelectSlot')}</ItemChangeText>
    </ItemContainer>
  );
};

function DeliverySummary(props) {
  const word = useLanguage();
  const dispatch = useDispatch();
  const { ThSite, MySite } = useSite();
  const deliveryData = useDeliveryData();
  const deliveryControl = useDeliveryControlFlow();
  const [titleHead, setTitleHead] = useState(word('DeliveryMethod.OnDemand'));
  const { cart } = useCarts();
  const warningStoreOpenTime = getStoreOpenTime();
  const displaySdNd = deliveryMethodResolver(deliveryData.activeDeliveryType, deliveryData.activeDeliveryMethod);
  const displayTimeSlot = deliveryDateTextResolver(cart?.selectedShippingMethod);
  // const displayTimeSlot = `(${deliveryData.activeStore.slot.fromTime} - ${deliveryData.activeStore.slot.toTime})`;
  const isOnDemand = deliveryData.deliveryType === 'OD';
  const { isMobile, isTablet, isTabletOrMobile } = useResponsive();
  const storeLocation = useStoreLocation();
  const collectionPointList = useMemo(() => {
    if (deliveryData.activeDeliveryMethod?.includes('CC')) {
      if (deliveryData.firstGetStoreCC) {
        deliveryControl.setFirstGetStoreCC(false);
        return storeLocation.storeData;
      }
      return null;
    }
    return storeLocation.storeData;
  });
  const currentStore = deliveryData.activeStore;
  const currentStoreType = currentStore?.storeType;
  
  const isActiveStorePermanantClose = useMemo(() => {
    if (collectionPointList) {
      const { stores } = collectionPointList;
      const isStoreActive = Boolean(stores.find((r) => r.id === deliveryData.activeStore.storeId));
      if (MySite && deliveryData.activeDeliveryType === 'SFL' && deliveryData.activeDeliveryMethod === 'HD') {
        if (stores.length > 0) {
          if (!isStoreActive) {
            props.onNext(SELECT_STORE);
          } else {
            return false;
          }
        } else {
          return true;
        }
      }
      return !isStoreActive;
    }
    return false;
  }, [collectionPointList]);

  useEffect(() => {
    if (configs.siteCode === 'TH') {
      if (deliveryData.activeDeliveryMethod === 'HDND' || deliveryData.activeDeliveryMethod === 'B2B') {
        setTitleHead(word('DeliveryAddress.Type.Options.HomeDelivery'));
      } else if (deliveryData.activeDeliveryMethod === 'CCND') {
        setTitleHead(word('DeliveryAddress.Type.Options.Collect'));
      }
    } else {
      if (isOnDemand) {
        setTitleHead(word('Header.Delivery.HDSD'));
      } else if (deliveryData.activeDeliveryMethod === 'CC') {
        setTitleHead(word('OrderSummary.Contact.Pickup'));
      } else if (deliveryData.activeDeliveryMethod === 'HD') {
        setTitleHead(word('DeliveryAddress.Type.Options.HomeDelivery'));
      }
    }
  }, [deliveryData.activeDeliveryMethod]);

  useEffect(() => {
    dispatch(deliveryUpdateForm('shippingAddress', deliveryData.activeShippingAddress));
    if (!deliveryData.activeDeliveryMethod.includes('CC')) {
      storeLocation.getStoreLocation(deliveryData.activeShippingAddress);
    }
    trackPageLoad('Change Delivery Method');
  }, []);

  return (
    <Layout
      title={titleHead}
      backButtonShown={deliveryControl.stackSize > 1}
      onBack={props.onBack}
      onClose={props.onClose}
      bottomSpace={isMobile ? 0 : isTablet ? 24 : 0}
      width={isTablet ? defaultModalSize.tablet.width : defaultModalSize.desktop.width}
      height={isTablet ? defaultModalSize.tablet.height : defaultModalSize.desktop.height}
    >
      <Container style={{ padding: isTablet ? '0 16px' : 0 }}>
        {warningStoreOpenTime.isClose && isOnDemand && (
          <Box mb={2}>
            <StoreCloseWarningV2 time={warningStoreOpenTime.time} mode="small" />
          </Box>
        )}
        {deliveryData.activeShippingAddress.addressNumber && (deliveryData.activeDeliveryMethod.includes('HD') || deliveryData.activeDeliveryMethod === 'B2B') && (
          <>
            <SummaryItem
              header={word('Slot.HomeDelivery.Address')}
              desc={deliveryData.activeAddressText}
              changeHandler={() => {
                dispatch(deliveryUpdateForm('filter', { storeType: is1HrStore(isPhoenix ? currentStore?.storeCode : currentStore?.id) ? 'go_fresh' : currentStoreType }));
                props.onNext(DELIVERY_ADDRESS);
              }}
              word={word('Slot.Button.Edit')}
              showWarning={MySite && isActiveStorePermanantClose}
              warningDesc={word('Store.Error.MissingInfo.Web.Sameday.MY')}
              id="edit-delivery-address"
            />
            <Space line={0.8} />
          </>
        )}
        {deliveryData.activeStore.storeId && (ThSite || (MySite && deliveryData.activeDeliveryMethod?.includes('CC'))) && (
          <>
            <SummaryItem
              header={deliveryData.activeStore.name}
              desc={deliveryData.activeStoreAddressText}
              changeHandler={() => {
                dispatch(deliveryUpdateForm('filter', { storeType: null }));
                props.onNext(SELECT_STORE);
              }}
              word={word('Slot.Button.Edit')}
              showWarning={isActiveStorePermanantClose}
              warningDesc={word('Store.Error.MissingInfo.Description')}
              id="edit-delivery-store"
            />
            <Space line={0.8} />
          </>
        )}
        <Display show={deliveryData.activeDeliveryType !== 'OD'}>
          <>
            <Display show={!deliveryData.activeStore.slot.isNull}>
              <SummaryItem
                header={
                  (deliveryData.activeDeliveryMethod.includes('CC')) ? word('Slot.CollectionTime') : word('Slot.DeliverTime')
                }
                desc={`${displaySdNd} ${displayTimeSlot}`}
                changeHandler={() => props.onNext(DELIVERY_SLOT)}
                word={word('Slot.Button.Edit')}
                id="edit-delivery-slot"
              />
            </Display>
            <Display show={deliveryData.activeStore.slot.isNull || deliveryData.activeStore.slot.date === ''}>
              <DeliverSlotIsEmpty changeHandler={() => props.onNext(DELIVERY_SLOT)} />
            </Display>
            <Space line={0.8} />
          </>
        </Display>
        <Display show={deliveryData.activeDeliveryType === 'OD'}>
          <>
            <SummaryItem
              header={word('Slot.DeliverTime')}
              desc={currentStoreType === 'hyper' && !is1HrStore(isPhoenix ? deliveryData?.currentStore?.storeCode : deliveryData?.currentStore?.id)
                ? word('OrderSummary.Contact.OnDemand.Web')
                : word('OrderSummary.Contact.OnDemand.Web.1hr')}
              showChange={false}
            />
            <Space line={0.8} />
          </>
        </Display>
        <ButtonContainer>
          <BtnCancel
            id="cancel-delivery-summary-btn"
            data-cta-name-en="Cancel"
            data-cta-name-th="ยกเลิก"
            data-cta-type="Button"
            onClick={props.onClose}
          >
            {word('General.Cancel')}
          </BtnCancel>
          {!isActiveStorePermanantClose && (
          <BtnSubmit
            id="confirm-delivery-summary-btn"
            data-cta-name-en="Confirm"
            data-cta-name-th="ยกเลิก"
            data-cta-type="Button"
            onClick={() => {
              const slotData = deliveryData.activeStore.slot;
              const trackingData = {
                selected_delivery_type: deliveryData.activeDeliveryType,
                selected_delivery_method: deliveryData.activeDeliveryMethod,
                selected_store_id: deliveryData.activeStore.storeId,
                selected_store_name: deliveryData.activeStore.name,
                selected_store_type: currentStoreType === 'hyper' ? 'HY' : 'GF',
                selected_time: '',
              };

              if (slotData?.fromTime && slotData?.toTime) {
                trackingData.selected_time = `${slotData.fromTime} - ${slotData.toTime}`;
              }
              trackClickConfirmSelectDelivery(trackingData);

              props.onNext(SUMMARY_DELIVERY);
            }}
          >
            {word('General.Confirm')}
          </BtnSubmit>
          )}
        </ButtonContainer>
      </Container>
    </Layout>
  );
}

export function SummaryItem({
  header, desc, changeHandler, word, showChange = true, showWarning = false, warningDesc, id, style
}) {
  return (
    <ItemContainer style={style}>
      <div style={{ flex: 1 }}>
        <ItemHeader>{header}</ItemHeader>
        <ItemDetail>{desc}</ItemDetail>
        {showWarning && (
        <ItemPermanantClosed>
          <InfoOutlinedIcon style={{ fontSize: 20 }} />
          <span style={{ paddingLeft: '4px' }}>{warningDesc}</span>
        </ItemPermanantClosed>
        )}
      </div>
      { showChange && (
        <ItemChangeText id={id} onClick={changeHandler}>{word}</ItemChangeText>
      )}
    </ItemContainer>
  );
}

export default DeliverySummary;
